import { useRef, useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Alert, App, Button, Form, Input, Radio, Select } from 'antd'
import {
  rentalOrderListSchema,
  serviceOrderListSchema,
  type OrderBatchUpdateBySearchInput,
  type OrderBatchUpdateInput,
  type OrderGroup
} from '@super-mega/server/contracts/order'
import { useRemoteKeyword } from '@/hooks/use-remote-keyword.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { useOrderBatchUpdateRefresh } from './hooks/use-order-batch-update-refresh.ts'
import { OrderGroupSelectorModal } from './order-group-selector-modal.tsx'

export type OrderBatchEdit = {
  kind: OrderBatchUpdateInput['change']['kind']
} & (
  | { orders: { id: number; customerId: number }[]; search?: never; expectedCount?: never }
  | {
      search: OrderBatchUpdateBySearchInput['search']
      expectedCount: number
      orders?: never
    }
)

type Values = {
  salespersonId?: number
  customerId?: number
  groupMode?: 'existing' | 'new'
  groupName?: string
  groupId?: number
}

export function OrderBatchUpdateModal({
  editing,
  orderKind,
  onClose,
  onSaved,
  onQueued
}: {
  editing: OrderBatchEdit
  orderKind: 'service' | 'rental'
  onClose: () => void
  onSaved: () => Promise<void>
  onQueued?: (jobId: string) => void
}) {
  const trpc = useTRPC()
  const refresh = useOrderBatchUpdateRefresh(onSaved)
  const { message } = App.useApp()
  const [form] = Form.useForm<Values>()
  const submissionKey = useRef<string | undefined>(undefined)
  const [group, setGroup] = useState<OrderGroup>()
  const [selectingGroup, setSelectingGroup] = useState(false)
  const groupMode = Form.useWatch('groupMode', form) ?? 'existing'
  const customerSearch = useRemoteKeyword()
  const salespersonSearch = useRemoteKeyword()
  const customers = useQuery({
    ...trpc.lookup.customers.queryOptions({
      purpose: orderKind === 'rental' ? 'order.rental.read' : 'order.service.read',
      keyword: customerSearch.keyword
    }),
    enabled: editing.kind === 'customer'
  })
  const salespeople = useQuery({
    ...trpc.lookup.members.queryOptions({
      purpose: orderKind === 'rental' ? 'order.rental.read' : 'order.service.read',
      keyword: salespersonSearch.keyword
    }),
    enabled: editing.kind === 'salesperson'
  })
  const service = useMutation(trpc.order.service.batchUpdate.mutationOptions())
  const rental = useMutation(trpc.order.rental.batchUpdate.mutationOptions())
  const serviceBySearch = useMutation(trpc.order.service.batchUpdateBySearch.mutationOptions())
  const rentalBySearch = useMutation(trpc.order.rental.batchUpdateBySearch.mutationOptions())
  const serviceSelection = useQuery({
    ...trpc.order.service.batchSelection.queryOptions(
      orderKind === 'service' && editing.search
        ? serviceOrderListSchema.parse(editing.search)
        : { current: 1, pageSize: 20 }
    ),
    enabled: Boolean(editing.search) && orderKind === 'service'
  })
  const rentalSelection = useQuery({
    ...trpc.order.rental.batchSelection.queryOptions(
      orderKind === 'rental' && editing.search
        ? rentalOrderListSchema.parse(editing.search)
        : { current: 1, pageSize: 20 }
    ),
    enabled: Boolean(editing.search) && orderKind === 'rental'
  })
  const selection = orderKind === 'rental' ? rentalSelection : serviceSelection
  const mutation = orderKind === 'rental' ? rental : service
  const pending = mutation.isPending || serviceBySearch.isPending || rentalBySearch.isPending
  const customerIds = new Set(editing.orders?.map((order) => order.customerId))
  let commonCustomerId: number | undefined
  if (editing.search) {
    commonCustomerId = selection.data?.commonCustomerId ?? undefined
  } else if (customerIds.size === 1) {
    commonCustomerId = editing.orders[0]?.customerId
  }
  const count = editing.search ? editing.expectedCount : editing.orders.length
  const selectionChanged = Boolean(editing.search && selection.data && selection.data.total !== count)
  const invalidGroup = editing.kind === 'group' && !commonCustomerId && (!editing.search || Boolean(selection.data))
  let title = '批量纳入订单组'
  if (editing.kind === 'salesperson') {
    title = '批量修改业务员'
  } else if (editing.kind === 'customer') {
    title = '批量修改客户'
  }
  let queryError = editing.search ? selection.error : null
  if (!queryError) {
    if (editing.kind === 'customer') {
      queryError = customers.error
    } else if (editing.kind === 'salesperson') {
      queryError = salespeople.error
    }
  }

  const retryOptionQuery = () => {
    if (editing.search && selection.error) {
      return selection.refetch()
    }
    if (editing.kind === 'customer') {
      return customers.refetch()
    }
    return salespeople.refetch()
  }

  const save = async (values: Values) => {
    if (invalidGroup || selectionChanged || (editing.search && !selection.data)) {
      return false
    }
    let change: OrderBatchUpdateInput['change']
    if (editing.kind === 'salesperson') {
      change = { kind: 'salesperson', salespersonId: values.salespersonId ?? null }
    } else if (editing.kind === 'customer') {
      if (!values.customerId) {
        return false
      }
      change = { kind: 'customer', customerId: values.customerId }
    } else {
      if (values.groupMode === 'existing' && !group) {
        return false
      }
      if (values.groupMode === 'new') {
        change = { kind: 'group', orderGroup: { mode: 'new', name: values.groupName?.trim() || undefined } }
      } else {
        if (!group) return false
        change = { kind: 'group', orderGroup: { mode: 'existing', id: group.id } }
      }
    }
    try {
      if (submissionKey.current === undefined) submissionKey.current = crypto.randomUUID()
      const payload = {
        idempotencyKey: submissionKey.current,
        change
      }
      let result
      if (editing.search && orderKind === 'rental') {
        result = await rentalBySearch.mutateAsync({
          ...payload,
          search: rentalOrderListSchema.parse(editing.search),
          expectedCount: editing.expectedCount
        })
      } else if (editing.search) {
        result = await serviceBySearch.mutateAsync({
          ...payload,
          search: serviceOrderListSchema.parse(editing.search),
          expectedCount: editing.expectedCount
        })
      } else {
        result = await mutation.mutateAsync({ ...payload, ids: editing.orders.map((order) => order.id) })
      }
      if (result.queued && result.jobId) {
        onQueued?.(result.jobId)
        void message.success(`已提交 ${result.count} 个订单的后台任务`)
        onClose()
        return true
      }
      void message.success(`已更新 ${result.count} 个订单`)
      await refresh()
      onClose()
      return true
    } catch (error) {
      void message.error(getErrorMessage(error, '批量修改失败'))
      return false
    }
  }

  return (
    <>
      <ModalForm<Values>
        open
        form={form}
        title={`${title} · ${count} 个订单`}
        width="36rem"
        layout="vertical"
        initialValues={{ groupMode: 'existing' }}
        onValuesChange={() => {
          submissionKey.current = undefined
        }}
        onFinish={save}
        onOpenChange={(open) => {
          if (!open) {
            onClose()
          }
        }}
        modalProps={{
          destroyOnHidden: true,
          mask: { closable: false },
          closable: !pending,
          onCancel: onClose
        }}
        submitter={{
          searchConfig: { submitText: '确认修改', resetText: '取消' },
          submitButtonProps: {
            loading: pending,
            disabled:
              invalidGroup || Boolean(queryError) || selectionChanged || Boolean(editing.search && !selection.data)
          },
          resetButtonProps: { disabled: pending, onClick: onClose }
        }}
      >
        {editing.search && (
          <p className="text-content-muted mb-4">将处理当前搜索条件下的全部 {count} 个订单，包含其他分页。</p>
        )}
        {selectionChanged && (
          <Alert className="mb-4" showIcon type="warning" title="搜索结果数量已变化，请关闭弹窗并刷新列表后重试。" />
        )}
        {queryError ? (
          <Alert
            className="mb-4"
            type="error"
            showIcon
            title={getErrorMessage(queryError, '选项加载失败')}
            action={<Button onClick={() => void retryOptionQuery()}>重试</Button>}
          />
        ) : null}
        {editing.kind === 'salesperson' ? (
          <Form.Item name="salespersonId" label="业务员" extra="留空将清除所选订单的业务员。">
            <Select
              allowClear
              disabled={pending}
              loading={salespeople.isFetching}
              options={salespeople.data ?? []}
              placeholder="选择业务员，或留空清除"
              showSearch={{ filterOption: false, onSearch: salespersonSearch.onSearch }}
            />
          </Form.Item>
        ) : null}
        {editing.kind === 'customer' ? (
          <>
            <Alert
              className="mb-4"
              showIcon
              type="info"
              title="更换客户后将移出原订单组，同步草稿账单和租赁占用，保留业务员及服务对象资料。已有非草稿账单或收款关联的订单不能更换客户；任一订单失败，整批不修改。"
            />
            <Form.Item name="customerId" label="新客户" rules={[{ required: true, message: '请选择客户' }]}>
              <Select
                disabled={pending}
                loading={customers.isFetching}
                options={customers.data ?? []}
                placeholder="请选择客户"
                showSearch={{ filterOption: false, onSearch: customerSearch.onSearch }}
              />
            </Form.Item>
          </>
        ) : null}
        {editing.kind === 'group' ? (
          invalidGroup ? (
            <Alert showIcon type="warning" title="所选订单属于不同客户，请先统一客户或选择同一客户的订单。" />
          ) : (
            <>
              <Form.Item name="groupMode" label="订单组方式">
                <Radio.Group
                  optionType="button"
                  buttonStyle="solid"
                  disabled={pending}
                  options={[
                    { label: '选择已有订单组', value: 'existing' },
                    { label: '创建新订单组', value: 'new' }
                  ]}
                />
              </Form.Item>
              {groupMode === 'new' ? (
                <Form.Item key="new-group" name="groupName" label="订单组名称（可选）" extra="留空自动生成订单组号。">
                  <Input disabled={pending} maxLength={255} />
                </Form.Item>
              ) : (
                <Form.Item
                  key="existing-group"
                  name="groupId"
                  label="已有订单组"
                  rules={[{ required: true, message: '请选择订单组' }]}
                >
                  <Button disabled={pending} onClick={() => setSelectingGroup(true)}>
                    {group?.name ?? '选择当前客户的订单组'}
                  </Button>
                </Form.Item>
              )}
            </>
          )
        ) : null}
      </ModalForm>
      {selectingGroup ? (
        <OrderGroupSelectorModal
          open
          customerId={commonCustomerId}
          initialSelectedGroup={group}
          onCancel={() => setSelectingGroup(false)}
          onSelect={(selected) => {
            setGroup(selected)
            form.setFieldValue('groupId', selected.id)
            submissionKey.current = undefined
            setSelectingGroup(false)
          }}
        />
      ) : null}
    </>
  )
}
